/**
 * Invoice → Finance producer helpers (Phase 2C).
 *
 * Deposit account is captured at invoice create (org default) and used at paid
 * time via Finance Ingestion Contract — never write movements directly.
 * Payment reversals post an opposing outbound event (immutable history).
 */

import { supabase } from "../supabase";
import {
  getEffectiveOrgSetting,
  organizationRowToAppSettings,
} from "./organizationSettings";
import {
  createInvoicePaymentEvent,
  createInvoicePaymentReversalEvent,
} from "./financeIngestion";
import { actorCanPostFinanceEvents } from "./nexoraOpsAccess";
import { resolveOrganizationIdForQueries } from "./orgQueryScope";

/**
 * Read org default deposit account from organizations.settings.
 */
export async function loadDefaultInvoiceDepositAccountId(
  actor,
  client = supabase,
) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);
  if (!organizationId) {
    return null;
  }

  const { data, error } = await client
    .from("organizations")
    .select("id, settings, company_name, legal_name, logo_url, address, phone, email, website, currency, timezone, locale, tax_settings")
    .eq("id", organizationId)
    .maybeSingle();

  if (error) {
    throw error;
  }
  if (!data) {
    return null;
  }

  const settings = organizationRowToAppSettings(data);
  const accountId = getEffectiveOrgSetting(
    settings,
    "modules.finance.default_invoice_deposit_account_id",
    null,
  );
  return accountId || null;
}

/**
 * Resolve deposit account for a payment: invoice field, then org default.
 */
export function resolveInvoiceDepositAccountId(invoice, orgDefaultAccountId = null) {
  return (
    invoice?.deposit_account_id ||
    invoice?.depositAccountId ||
    orgDefaultAccountId ||
    null
  );
}

export function invoicePaymentIdempotencyKey(invoiceId, cycle = 0) {
  const id = String(invoiceId || "").trim();
  if (!id) {
    throw new Error("Invoice id is required for payment idempotency.");
  }
  const n = Number(cycle) || 0;
  return n <= 0 ? `invoice-payment:${id}` : `invoice-payment:${id}:c${n}`;
}

export function invoicePaymentReversalIdempotencyKey(paymentEventId) {
  const id = String(paymentEventId || "").trim();
  if (!id) {
    throw new Error("Payment event id is required for reversal idempotency.");
  }
  return `invoice-payment-reversal:${id}`;
}

/** Finance idempotency for one invoice_payments receipt. Instalments do not share a key. */
export function invoiceReceiptFinanceIdempotencyKey(paymentId) {
  const id = String(paymentId || "").trim();
  if (!id) {
    throw new Error("Payment id is required for receipt idempotency.");
  }
  return `invoice-payment:${id}`;
}

export function invoiceReceiptReversalFinanceIdempotencyKey(paymentId) {
  const id = String(paymentId || "").trim();
  if (!id) {
    throw new Error("Payment id is required for receipt reversal idempotency.");
  }
  return `invoice-payment-reversal:${id}`;
}

function clientDisplayName(invoice) {
  return (
    invoice?.clients?.client_name ||
    invoice?.client_name ||
    invoice?.title ||
    "Customer"
  );
}

function isPaymentReversalEvent(event) {
  if (!event) return false;
  if (event.reversal_of_event_id) return true;
  const action = event.metadata?.action;
  return action === "payment_reversal";
}

/**
 * Load invoice-sourced payment events for an invoice (inbound + reversals).
 */
export async function listInvoicePaymentFinanceEvents(
  client,
  organizationId,
  invoiceId,
) {
  if (!client || !organizationId || !invoiceId) {
    return [];
  }

  const { data, error } = await client
    .from("finance_events")
    .select(
      `
      id,
      event_type,
      status,
      occurred_on,
      posted_at,
      created_at,
      idempotency_key,
      source_id,
      source_ref,
      reversal_of_event_id,
      metadata,
      description,
      notes,
      finance_movements (
        id,
        flow,
        amount,
        currency,
        account_id
      )
    `,
    )
    .eq("organization_id", organizationId)
    .eq("source_system", "invoices")
    .eq("event_type", "invoice_payment")
    .eq("source_id", invoiceId)
    .neq("status", "voided")
    .order("created_at", { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Find the open (unreversed) inbound payment event for an invoice, if any.
 */
export function findOpenInvoicePaymentEvent(events = []) {
  const rows = Array.isArray(events) ? events : [];
  const reversedIds = new Set(
    rows
      .filter((event) => event?.reversal_of_event_id)
      .map((event) => event.reversal_of_event_id),
  );

  const openInbound = rows.filter((event) => {
    if (isPaymentReversalEvent(event)) return false;
    if (reversedIds.has(event.id)) return false;
    const movements = event.finance_movements || [];
    const hasInbound = movements.some((m) => m.flow === "inbound");
    // Legacy rows may omit nested movements in some queries — treat as inbound payment.
    return hasInbound || movements.length === 0;
  });

  return openInbound.length ? openInbound[openInbound.length - 1] : null;
}

/**
 * How many completed payment cycles (payments that were later reversed).
 * Next Mark Paid uses cycle = completedCycles (0 → legacy key).
 */
export function nextInvoicePaymentCycle(events = []) {
  const rows = Array.isArray(events) ? events : [];
  const reversalCount = rows.filter((event) =>
    isPaymentReversalEvent(event),
  ).length;
  return reversalCount;
}

function resolvePaymentMovementAccount(event, fallbackAccountId) {
  const movements = event?.finance_movements || [];
  const inbound = movements.find((m) => m.flow === "inbound");
  return inbound?.account_id || fallbackAccountId || null;
}

function resolvePaymentMovementAmount(event, fallbackAmount) {
  const movements = event?.finance_movements || [];
  const inbound = movements.find((m) => m.flow === "inbound");
  if (inbound && Number.isFinite(Number(inbound.amount))) {
    return Number(inbound.amount);
  }
  return Number(fallbackAmount);
}

/**
 * Post one receipt into Finance.
 * Cash amount is the receipt (`payment_amount`), never the invoice total.
 * Live recording posts inside `cp_record_invoice_payment` so this helper is
 * not on the invoice save path. Calling it without a payment id does not post.
 *
 * @returns {{ posted: boolean, eventId?: string, skipped?: string, idempotentReplay?: boolean }}
 */
export async function postInvoicePaymentToFinance(
  client,
  actor,
  invoice,
  { occurredOn = null, orgDefaultAccountId = null } = {},
) {
  if (!invoice?.invoice_id) {
    throw new Error("Invoice is required to post payment to Finance.");
  }

  if (!invoice.payment_id) {
    return { posted: false, skipped: "invoice_payments_ledger" };
  }

  const financeAvailable = await actorCanPostFinanceEvents(client);
  if (!financeAvailable) {
    return { posted: false, skipped: "finance_disabled" };
  }

  const depositAccountId = resolveInvoiceDepositAccountId(
    invoice,
    orgDefaultAccountId,
  );
  if (!depositAccountId) {
    throw new Error(
      "This invoice has no Deposit Account. Set Deposit Account on the invoice (or the organization default) before marking it paid.",
    );
  }

  const organizationId =
    invoice.organization_id ||
    (await resolveOrganizationIdForQueries(actor, client));
  if (!organizationId) {
    throw new Error("Unable to resolve organization for Finance payment.");
  }

  const amount = Number(invoice.payment_amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Payment amount is invalid for Finance.");
  }

  const invoiceNumber = invoice.invoice_number || invoice.invoice_id;
  const clientName = clientDisplayName(invoice);
  const paidDate =
    occurredOn ||
    (invoice.paid_at ? String(invoice.paid_at).slice(0, 10) : null) ||
    new Date().toISOString().slice(0, 10);

  const result = await createInvoicePaymentEvent(client, {
    organization_id: organizationId,
    account_id: depositAccountId,
    amount,
    occurred_on: paidDate,
    description: `${clientName} · Invoice ${invoiceNumber}`,
    notes: invoice.payment_notes || null,
    idempotency_key: invoiceReceiptFinanceIdempotencyKey(invoice.payment_id),
    source_id: invoice.invoice_id,
    source_ref: invoiceNumber,
    metadata: {
      invoice_id: invoice.invoice_id,
      invoice_number: invoiceNumber,
      client_name: clientName,
      payment_id: invoice.payment_id,
      payment_method: invoice.payment_method || null,
      payment_reference: invoice.payment_reference || null,
    },
  });

  return {
    posted: true,
    eventId: result.eventId,
    idempotentReplay: result.idempotentReplay === true,
  };
}

/**
 * Reverse the Finance event for one receipt.
 * Live reversals post inside `cp_reverse_invoice_payment`.
 *
 * @returns {{ posted: boolean, eventId?: string, skipped?: string, reversedEventId?: string }}
 */
export async function reverseInvoicePaymentInFinance(
  client,
  actor,
  invoice,
  { occurredOn = null, orgDefaultAccountId = null, paymentId = null } = {},
) {
  if (!invoice?.invoice_id) {
    throw new Error("Invoice is required to reverse payment in Finance.");
  }

  const receiptId = paymentId || invoice.payment_id;
  if (!receiptId) {
    return { posted: false, skipped: "invoice_payments_ledger" };
  }

  const financeAvailable = await actorCanPostFinanceEvents(client);
  if (!financeAvailable) {
    return { posted: false, skipped: "finance_disabled" };
  }

  const organizationId =
    invoice.organization_id ||
    (await resolveOrganizationIdForQueries(actor, client));
  if (!organizationId) {
    throw new Error("Unable to resolve organization for Finance reversal.");
  }

  const depositAccountId = resolveInvoiceDepositAccountId(
    invoice,
    orgDefaultAccountId,
  );

  const events = await listInvoicePaymentFinanceEvents(
    client,
    organizationId,
    invoice.invoice_id,
  );
  const receiptKey = invoiceReceiptFinanceIdempotencyKey(receiptId);
  const openPayment =
    events.find((event) => event?.idempotency_key === receiptKey) || null;

  if (!openPayment?.id || isPaymentReversalEvent(openPayment)) {
    return { posted: false, skipped: "no_open_payment_event" };
  }

  const accountId = resolvePaymentMovementAccount(
    openPayment,
    depositAccountId,
  );
  if (!accountId) {
    throw new Error(
      "Cannot reverse payment: deposit account is missing on the original Finance event and the invoice.",
    );
  }

  const amount = resolvePaymentMovementAmount(openPayment, invoice.payment_amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Cannot reverse payment: original payment amount is invalid.");
  }

  const invoiceNumber = invoice.invoice_number || invoice.invoice_id;
  const clientName = clientDisplayName(invoice);
  const reverseDate =
    occurredOn || new Date().toISOString().slice(0, 10);

  const result = await createInvoicePaymentReversalEvent(client, {
    organization_id: organizationId,
    account_id: accountId,
    amount,
    occurred_on: reverseDate,
    description: `Payment reversal · ${clientName} · Invoice ${invoiceNumber}`,
    notes: "Reverse Payment",
    idempotency_key: invoiceReceiptReversalFinanceIdempotencyKey(receiptId),
    source_id: invoice.invoice_id,
    source_ref: invoiceNumber,
    reversal_of_event_id: openPayment.id,
    metadata: {
      invoice_id: invoice.invoice_id,
      invoice_number: invoiceNumber,
      client_name: clientName,
      reversed_event_id: openPayment.id,
      reversed_at: new Date().toISOString(),
    },
  });

  return {
    posted: true,
    eventId: result.eventId,
    reversedEventId: openPayment.id,
    idempotentReplay: result.idempotentReplay === true,
  };
}
